import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import { collection, getDocs, updateDoc, doc } from 'firebase/firestore';
import { getUserRole, ROLES } from '../utils/roles';
import { useT, useLang } from '../i18n';
import { tarikhPapar } from '../utils/tarikhPapar';
import { PageHeader, Card, CardBody, Btn, Badge, Segmented, IconBtn } from '../design/ui';
import { CirclePlay, Menu, Pin, PinOff, ExternalLink, RotateCw } from 'lucide-react';
import FlowT2Kredit from './FlowT2Kredit';
import FlowBrowserLain from './FlowBrowserLain';
import { lepasBrowser } from '../services/flowBrowserService';
import { ayatRalatT2, kodT2Sah } from '../utils/flowT2Ralat';
import FlowGagalLogin from './FlowGagalLogin';
import './FlowCustomerView.css';

const API = import.meta.env.VITE_FLOW_SESSION_API || 'https://asia-southeast1-ai8saat.cloudfunctions.net/flowSessionPilot';
function openFlow(uid) {
    return new Promise((resolve, reject) => {
        const requestId = crypto.randomUUID();
        const clean = () => { clearTimeout(timer); window.removeEventListener('message', receive); };
        const receive = event => {
            if (event.source !== window || event.origin !== location.origin || event.data?.type !== 'SESAAT_FLOW_CUSTOMER_RESULT' || event.data.requestId !== requestId) return;
            clean(); event.data.success ? resolve() : reject(new Error(event.data.code));
        };
        // 150 s (was 90): the extension may first wait up to ~60 s for a fresh session copy
        // (server: functions/flowSessionSegar.js) before the usual restore + reload.
        const timer = setTimeout(() => { clean(); reject(new Error('EXTENSION_TIMEOUT')); }, 150000);
        window.addEventListener('message', receive);
        window.postMessage({ type: 'SESAAT_FLOW_CUSTOMER_OPEN', requestId, uid }, location.origin);
    });
}
// Reasons the extension reports when the customer's OWN browser rejected a good
// session (cookies restored, but Google still asked to sign in / storage blocked).
// These mean "your browser", not "session dead" — the card tells them apart.
const BROWSER_REJECT = new Set(['RESTORE_STILL_LOGIN', 'RESTORE_NOT_AUTHENTICATED', 'COOKIE_RESTORE_MISMATCH', 'FLOW_STORAGE_RESTORE_FAILED']);
const FP_PAGE_SIZE = 5;
// Butang "Buka" = page PROJECT, bukan collection: potong selepas id project. Rekod lama tersimpan
// sebagai link penuh (…/project/{id}/collection/{id}). Link yang tidak dikenali → tiada butang Buka
// (data datang dari halaman luar — jangan jadikan apa-apa teks sebagai href).
// TWIN: public/scripts/flow-session-privacy.js `_projectHref` (potongan sama semasa menyimpan).
const linkProjek = href => (String(href || '').match(/^(https:\/\/[^/?#]+[^?#]*?\/project\/[a-zA-Z0-9_-]{4,64})(?=[/?#]|$)/) || [])[1] || null;
// Customer-facing status: colour + which message explains WHY (esp. source_down,
// so the customer waits for admin re-login instead of hammering Open Flow).
const STATE = {
    ready: ['ok', 'fc.st.ready'],
    source_down: ['bad', 'fc.st.sourceDown'],
    preparing: ['warn', 'fc.st.preparing'],
    disabled: ['warn', 'fc.st.disabled'],
    maintenance: ['warn', 'fc.st.maintenance'],
    busy: ['warn', 'fc.st.busy'],
    expired: ['idle', 'fc.st.expired'],
    no_access: ['idle', 'fc.st.noAccess'],
    credit_limit: ['bad', 'fc.st.creditLimit'],   // Jenis 2: had credit dicapai → hubungi admin (flowT2Login.js status)
};

export default function FlowCustomerView({ onMenuClick, initialTab = null }) {
    // Tab ikut URL (deep-link): /flow-google → overview · /flow-google/billing · /flow-google/settings
    // (route App.jsx hantar `initialTab`). Tukar tab = navigate → URL + tab segerak, boleh share.
    const t = useT(), navigate = useNavigate(), tab = initialTab || 'overview', [data, setData] = useState(null);
    const { lang } = useLang();   // bahasa page → format tarikh (utils/tarikhPapar.js)
    const [loading, setLoading] = useState(true), [busy, setBusy] = useState(false), [message, setMessage] = useState(''), [error, setError] = useState('');
    // On-demand refresh: 'idle' → 'refreshing' (asked the PC for a fresh session) →
    // 'ready' (a new one landed) or 'timeout' (still not ready after ~2.5 min).
    const [rf, setRf] = useState('idle'), [refreshBusy, setRefreshBusy] = useState(false);
    // Satu Flow, satu browser: true selepas extension menjawab ACTIVE_ELSEWHERE (FlowBrowserLain.jsx).
    const [lain, setLain] = useState(false);
    // Jenis 2: kod sebab login gagal (T2_*) → popup FlowGagalLogin. Had credit TIDAK di sini (ada baris status sendiri).
    const [gagalT2, setGagalT2] = useState(null);
    const laporGagalT2 = kod => (kod === 'T2_HAD_KREDIT' ? setError(ayatRalatT2(kod, t)) : setGagalT2(kod));
    // Flow Projects state
    const [projects, setProjects] = useState(null); // null = belum muatkan
    const [fpSearch, setFpSearch] = useState('');
    const [fpSort, setFpSort] = useState('newest');
    const [fpPage, setFpPage] = useState(1);
    const refresh = useCallback(async () => {
        setLoading(true); setError('');
        try {
            await auth.authStateReady();
            if (!auth.currentUser) throw new Error();
            const r = await fetch(API + '/customer/overview', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + await auth.currentUser.getIdToken() }, body: '{}', credentials: 'omit', cache: 'no-store', signal: AbortSignal.timeout(30000) });
            if (!r.ok) throw new Error();
            setData(await r.json());
        } catch { setError(t('fc.loadError')); } finally { setLoading(false); }
    }, [t]);
    useEffect(() => { void refresh(); }, [refresh]);
    // Tab login Jenis 2 yang gagal dibawa balik ke sini dengan ?gagal=KOD (skrip flow-t2-login.js,
    // extension lama sahaja). Kod disahkan bentuknya, dipapar sebagai ayat, lalu dibuang dari alamat.
    useEffect(() => {
        const q = new URLSearchParams(window.location.search), kod = q.get('gagal');
        if (kod === null) return;
        if (kodT2Sah(kod)) laporGagalT2(kod);
        q.delete('gagal');
        window.history.replaceState(null, '', window.location.pathname + (q.toString() ? '?' + q : '') + window.location.hash);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Silent poll so the status recovers on its own — when the admin re-logs the
    // Gmail in, this flips back to ready within ~45s without the customer refreshing.
    const refreshSilent = useCallback(async () => {
        try {
            await auth.authStateReady();
            if (!auth.currentUser) return;
            const r = await fetch(API + '/customer/overview', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + await auth.currentUser.getIdToken() }, body: '{}', credentials: 'omit', cache: 'no-store', signal: AbortSignal.timeout(30000) });
            if (r.ok) setData(await r.json());
        } catch { /* keep last known status */ }
    }, []);
    useEffect(() => { const id = setInterval(() => void refreshSilent(), 45000); return () => clearInterval(id); }, [refreshSilent]);

    // While a refresh is in flight, poll fast (every 5s) and give up after ~2.5 min.
    useEffect(() => {
        if (rf !== 'refreshing') return;
        const poll = setInterval(() => void refreshSilent(), 5000);
        const to = setTimeout(() => setRf(p => p === 'refreshing' ? 'timeout' : p), 150000);
        return () => { clearInterval(poll); clearTimeout(to); };
    }, [rf, refreshSilent]);
    // "Fresh copy first" (functions/flowSessionSegar.js): Open Flow may be waiting while the owner
    // PC captures a new session copy. Poll fast while Open Flow runs, or while a capture is in
    // flight, so the "preparing a new session" line appears and clears on its own — the customer
    // is told WHY it is taking longer instead of watching a bare spinner. Bounded: `busy` ends
    // with the extension's answer, and the server stops reporting a request as pending after 2 min.
    const sedangSegar = !!data?.self?.refreshPending;
    useEffect(() => {
        if (!(busy || sedangSegar) || rf === 'refreshing') return; // rf 'refreshing' already polls (above)
        const poll = setInterval(() => void refreshSilent(), 5000);
        return () => clearInterval(poll);
    }, [busy, sedangSegar, rf, refreshSilent]);
    // A fresh session landed → ready; the session died again while we showed ready → reset.
    useEffect(() => {
        const available = !!data?.access?.available;
        if (rf === 'refreshing' && available) setRf('ready');
        else if (rf === 'ready' && !available) setRf('idle');
    }, [data, rf]);

    // Ask the owner PC to capture a fresh pool session now (coalesced server-side).
    async function handleRefresh() {
        if (refreshBusy || !auth.currentUser) return;
        setRefreshBusy(true); setError(''); setMessage('');
        try {
            const r = await fetch(API + '/customer/refresh', { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + await auth.currentUser.getIdToken() }, body: '{}', credentials: 'omit', cache: 'no-store', signal: AbortSignal.timeout(30000) });
            if (!r.ok) throw new Error();
            setRf('refreshing'); void refreshSilent();
        } catch { setError(t('fc.rf.requestError')); }
        finally { setRefreshBusy(false); }
    }

    // Muatkan Flow Projects dari Firestore apabila tab overview dibuka
    const loadProjects = useCallback(async () => {
        if (!auth.currentUser) return;
        const snap = await getDocs(collection(db, 'users', auth.currentUser.uid, 'flowProjects'));
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setProjects(list);
    }, []);
    useEffect(() => { if (tab === 'overview') void loadProjects(); }, [tab, loadProjects]);

    // Pin toggle — tulis terus ke Firestore dari webapp
    async function togglePin(projectId, current) {
        if (!auth.currentUser) return;
        const ref = doc(db, 'users', auth.currentUser.uid, 'flowProjects', projectId);
        await updateDoc(ref, { pinned: !current });
        setProjects(prev => prev.map(p => p.id === projectId ? { ...p, pinned: !current } : p));
    }

    async function launch() {
        if (busy || !auth.currentUser) return;
        setBusy(true); setError(''); setGagalT2(null); setRf('idle'); setMessage(t('fc.preparing'));
        try { await openFlow(auth.currentUser.uid); setLain(false); setMessage(t('fc.opened')); }
        catch (e) {
            setMessage('');
            // Flow dipegang browser lain → bukan ralat: papar notis + butang "Pindah ke browser ini".
            if (e.message === 'ACTIVE_ELSEWHERE') { setLain(true); void refreshSilent(); }
            else if (kodT2Sah(e.message)) laporGagalT2(e.message);   // login Jenis 2 gagal → popup dengan sebab + tindakan
            // Re-read the card at once: if a fresh copy is being captured (older extension that
            // cannot wait by itself), the "preparing a new session" line shows why and when to retry.
            else { setError(pesanGagalBuka(e)); void refreshSilent(); }
        }
        finally { setBusy(false); }
    }
    // ⚠️ Kod yang dipetakan di sini mesti juga disenaraikan dalam jambatan extension
    // (flowPilotBridge.ts) dan pilot.js — kod yang tiada di sana tiba sebagai FLOW_UNAVAILABLE.
    const pesanGagalBuka = e => (/^T2_/.test(e.message || '') ? ayatRalatT2(e.message, t) : t('fc.' + ({ EXTENSION_TIMEOUT: 'timeout', SESAAT_LOGIN_REQUIRED: 'login', PILOT_ACCESS_REQUIRED: 'noAccess', BUSY: 'busy', CAPACITY_FULL: 'busy' }[e.message] || 'openError')));
    // "Pindah ke browser ini": lepaskan browser lama di server, kemudian Open Flow di sini.
    // Dua langkah, satu klik — mesej + spinner bertukar ikut langkah supaya pelanggan tahu ia sedang berjalan.
    async function pindah() {
        if (busy || !auth.currentUser) return;
        setBusy(true); setError(''); setRf('idle'); setMessage(t('fc.br.moving'));
        try {
            await lepasBrowser();
            setMessage(t('fc.preparing'));
            await openFlow(auth.currentUser.uid);
            setLain(false); setMessage(t('fc.opened')); void refreshSilent();
        } catch (e) {
            setMessage('');
            setError(e.message === 'RELEASE_FAILED' || e.message === 'ACTIVE_ELSEWHERE' ? t('fc.br.moveError') : pesanGagalBuka(e));
        } finally { setBusy(false); }
    }
    // Bulan berhuruf ikut bahasa page ("2 Nov 2026") — tanpa locale ia ikut bahasa BROWSER
    // ("11/2/2026" = 2 Nov, tetapi dibaca 11 Feb di Malaysia). utils/tarikhPapar.js.
    const date = value => tarikhPapar(value, lang);
    // "View plans and payment" leads to the Flow Ultra billing page — admin-only for now.
    const isAdmin = getUserRole(auth.currentUser?.email) === ROLES.ADMIN;
    // Jenis 2: tarikh tamat + lencana ikut borang Jenis 2 (flowT2_akses.endAt), BUKAN rekod langganan
    // Flow biasa — dahulu pelanggan Jenis 2 boleh nampak dua tarikh berbeza (kad atas lawan kotak credit).
    const t2Tamat = data?.access?.kind === 'login' && Number.isSafeInteger(data.access.t2?.endAt) && data.access.t2.endAt > 0 ? data.access.t2.endAt : null;
    const tarikhTamat = t2Tamat ?? data?.subscription?.expiresAt;
    const langgananAktif = t2Tamat ? t2Tamat > Date.now() : !!data?.subscription?.active;
    return <main className="sesaat-m-tight fc-page" style={{ padding: '26px 28px 48px' }}>
        {onMenuClick && <div className="md:hidden" style={{ marginBottom: 12 }}><IconBtn icon={<Menu size={20} strokeWidth={1.9} />} label={t('fc.menu')} onClick={onMenuClick} /></div>}
        <PageHeader title="Flow Google" subtitle={t('fc.subtitle')} sisiTajuk={<CirclePlay size={18} strokeWidth={1.8} style={{ color: 'var(--s-ink-3)' }} />} />
        <Card aria-busy={loading || busy} style={{ marginBottom: 20 }}><CardBody>
            <Badge>{loading ? t('fc.loading') : langgananAktif ? t('fc.active') : data?.access?.pilot ? t('fc.pilot') : t('fc.inactive')}</Badge>
            <h2>{data?.subscription?.productName || 'Google Flow'}</h2><p>{t('fc.expiry')}: {date(tarikhTamat)}</p>
            {(() => {
                const accx = data?.access, available = !loading && !!accx?.available, reason = accx?.reason;
                const browserReject = BROWSER_REJECT.has(data?.self?.lastReason);
                const refreshing = rf === 'refreshing' || !!data?.self?.refreshPending;
                // Offer Refresh when the session is dead (source down) or the customer's own
                // last attempt was rejected by their browser, or a refresh timed out.
                const canRefresh = !loading && accx?.pilot && !refreshing && ((reason === 'source_down' && !available) || browserReject || rf === 'timeout');
                // One status line, in priority order.
                let tone = 'idle', key = null;
                if (refreshing) { tone = 'warn'; key = 'fc.rf.refreshing'; }
                else if (rf === 'ready') { tone = 'ok'; key = 'fc.rf.ready'; }
                else if (rf === 'timeout') { tone = 'bad'; key = 'fc.rf.timeout'; }
                else if (browserReject) { tone = available ? 'warn' : 'bad'; key = 'fc.rf.browserReject'; }
                else if (reason === 'source_down') { tone = 'bad'; key = 'fc.rf.dead'; }
                else if (reason === 'maintenance' || reason === 'disabled') { tone = 'bad'; key = 'fc.rf.maintenance'; }
                else if (!loading && reason && reason !== 'ready') { const s = STATE[reason] || ['idle', 'fc.st.noAccess']; tone = s[0]; key = s[1]; }
                return <>
                    <div className="fc-actions">
                        <Btn disabled={loading || busy || !available || refreshing} onClick={launch}>{busy ? t('fc.preparing') : 'Open Flow'}</Btn>
                        {canRefresh && <Btn variant="ghost" disabled={refreshBusy} onClick={handleRefresh}><RotateCw size={15} />{t('fc.rf.button')}</Btn>}
                        {isAdmin && <Link className="s-btn s-btn--ghost" to="/flow">{t('fc.renew')}</Link>}
                    </div>
                    {key && <p className={`fc-state fc-state--${tone}`} role="status">{refreshing ? <span className="fc-spinner" aria-hidden="true" /> : <span className="fc-state-dot" aria-hidden="true" />}{t(key)}</p>}
                </>;
            })()}
            {lain && <FlowBrowserLain seenAt={data?.browser?.seenAt} busy={busy} onMove={pindah} />}
            <p className="fc-muted">{t('fc.accessNote')}</p>
            {/* While the "preparing a new session" status line above is showing, it replaces the generic busy line (one spinner, one reason). */}
            {message && !(busy && sedangSegar) && <p role="status">{busy && <span className="fc-spinner" aria-hidden="true" />}{message}</p>}
            {error && <p className="fc-error" role="alert">{error}</p>}
        </CardBody></Card>
        <nav style={{ marginBottom: 20 }} aria-label={t('fc.sections')}><Segmented value={tab} onChange={key => navigate('/flow-google' + (key === 'overview' ? '' : '/' + key))} options={['overview', 'billing', 'settings'].map(key => ({ value: key, label: t('fc.' + key) }))} /></nav>
        <Card><CardBody>
            {tab === 'overview' && (() => {
                const all = projects || [];
                // Pinned dulu, kemudian ikut sort
                const filtered = all.filter(p => {
                    if (!fpSearch.trim()) return true;
                    const q = fpSearch.toLowerCase();
                    return (p.href || '').toLowerCase().includes(q) || (p.pageTitle || '').toLowerCase().includes(q) || p.id.toLowerCase().includes(q);
                });
                const sorted = [...filtered].sort((a, b) => {
                    // Pinned naik ke atas
                    if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
                    if (fpSort === 'newest') return (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0);
                    if (fpSort === 'oldest') return (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0);
                    // lastUsed
                    return (b.lastUsedAt?.seconds || 0) - (a.lastUsedAt?.seconds || 0);
                });
                const totalPages = Math.max(1, Math.ceil(sorted.length / FP_PAGE_SIZE));
                const safePage = Math.min(fpPage, totalPages);
                const paged = sorted.slice((safePage - 1) * FP_PAGE_SIZE, safePage * FP_PAGE_SIZE);
                const fmt = ts => ts?.seconds ? tarikhPapar(ts.seconds * 1000, lang) : '—';
                return <>
                    <FlowT2Kredit access={data?.access} />
                    <div className="fc-fp-head">
                        <h2>{t('fc.fp.title')}</h2>
                        <div className="fc-fp-toolbar">
                            <input className="fc-fp-search" value={fpSearch} onChange={e => { setFpSearch(e.target.value); setFpPage(1); }} placeholder={t('fc.fp.search')} />
                            <select className="fc-fp-sort" value={fpSort} onChange={e => { setFpSort(e.target.value); setFpPage(1); }}>
                                <option value="newest">{t('fc.fp.sortNewest')}</option>
                                <option value="oldest">{t('fc.fp.sortOldest')}</option>
                                <option value="lastUsed">{t('fc.fp.sortLastUsed')}</option>
                            </select>
                        </div>
                    </div>
                    {projects === null ? <p className="fc-muted"><span className="fc-spinner" aria-hidden="true" /></p>
                    : sorted.length === 0 ? <p className="fc-muted">{t('fc.fp.empty')}</p>
                    : <ul className="fc-fp-list">
                        {paged.map(p => {
                            const label = p.pageTitle || p.id;
                            return <li key={p.id} className={'fc-fp-item' + (p.pinned ? ' fc-fp-item--pinned' : '')}>
                                <div className="fc-fp-item-main">
                                    <span className="fc-fp-label" title={p.href}>{label}</span>
                                    <span className="fc-fp-meta">{t('fc.fp.account')}: {p.flowAccountName || '—'} · {fmt(p.createdAt)}</span>
                                </div>
                                <div className="fc-fp-item-actions">
                                    <IconBtn icon={p.pinned ? <PinOff size={14} /> : <Pin size={14} />} label={p.pinned ? t('fc.fp.unpin') : t('fc.fp.pin')} onClick={() => togglePin(p.id, p.pinned)} />
                                    {linkProjek(p.href) && <a className="s-btn s-btn--ghost fc-fp-open" href={linkProjek(p.href)} target="_blank" rel="noopener noreferrer"><ExternalLink size={13} />{t('fc.fp.open')}</a>}
                                </div>
                            </li>;
                        })}
                    </ul>}
                    {totalPages > 1 && <div className="fc-fp-pagination">
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(n =>
                            <button key={n} className={'fc-fp-pg' + (n === safePage ? ' fc-fp-pg--active' : '')} onClick={() => setFpPage(n)}>{n}</button>
                        )}
                    </div>}
                    <p className="fc-muted fc-fp-footnote">{t('fc.fp.note')}</p>
                </>;
            })()}
            {tab === 'billing' && <>
                <div className="fc-billing-head"><h2>{t('fc.billing')}</h2>{isAdmin && <Link className="s-btn s-btn--ghost" to="/flow">{t('fc.viewPlan')}</Link>}</div>
                {/* Langganan anda — SETIAP langganan: nama plan, jenis, tarikh, status, harga, credit (Type 2).
                    Jenis 2: tarikh tamat + credit ikut borang Type 2 (access.t2), bukan rekod flow biasa. */}
                {data?.subscriptions?.length ? <><h3 style={{ marginTop: 20 }}>{t('fc.yourPlans')}</h3><div className="fc-subs">
                    {data.subscriptions.map(s => {
                        const t2 = s.jenis === 2 ? data?.access?.t2 : null;
                        const exp = t2 && Number.isSafeInteger(t2.endAt) && t2.endAt > 0 ? t2.endAt : s.expiresAt;
                        const aktif = exp ? exp > Date.now() : s.active;
                        const hari = exp ? Math.ceil((exp - Date.now()) / 86400000) : null;
                        const stat = !exp ? null : !aktif ? ['stop', 'fc.subExpired'] : hari <= 7 ? ['warn', 'fc.subExpiring'] : ['ok', 'fc.subActive'];
                        const kredit = t2 && Number.isSafeInteger(t2.had) && t2.had > 0
                            ? `${Math.round(Number(t2.dahGuna) || 0).toLocaleString('en-MY')} / ${t2.had.toLocaleString('en-MY')}` : null;
                        return <div key={s.id} className="fc-sub">
                            <div className="fc-sub-top">
                                <span className="fc-sub-name">{s.productName || t(s.jenis === 2 ? 'fc.planType2' : 'fc.planType1')}</span>
                                {stat && <Badge tone={stat[0]}>{t(stat[1])}</Badge>}
                            </div>
                            <div className="fc-sub-meta">
                                <span><b>{t('fc.type')}:</b> {t(s.jenis === 2 ? 'fc.type2' : 'fc.type1')}</span>
                                {s.startedAt && <span><b>{t('fc.started')}:</b> {date(s.startedAt)}</span>}
                                {exp && <span><b>{t('fc.expiry')}:</b> {date(exp)}{hari != null && hari >= 0 ? ` · ${hari} ${t('fc.daysLeft')}` : ''}</span>}
                                {s.priceSen != null && <span><b>{t('fc.price')}:</b> {new Intl.NumberFormat('en-MY', { style: 'currency', currency: 'MYR' }).format(s.priceSen / 100)}</span>}
                                {kredit && <span><b>{t('fc.credits')}:</b> {kredit}</span>}
                            </div>
                        </div>;
                    })}
                </div></> : null}
                <h3 style={{ marginTop: 20 }}>{t('fc.history')}</h3>
                {data?.payments?.length ? <div className="fc-table"><table><thead><tr><th>{t('fc.date')}</th><th>{t('fc.amount')}</th><th>{t('fc.status')}</th></tr></thead><tbody>{data.payments.map(p => <tr key={p.id}><td>{date(p.at)}</td><td>{p.amountSen == null ? '—' : new Intl.NumberFormat('en-MY', { style: 'currency', currency: 'MYR' }).format(p.amountSen / 100)}</td><td>{t(p.manual ? (p.paid ? 'fc.paidManual' : 'fc.granted') : p.paid ? 'fc.paid' : 'fc.unpaid')}</td></tr>)}</tbody></table></div> : <p className="fc-muted">{t('fc.noPayments')}</p>}
            </>}
            {tab === 'settings' && <><h2>{t('fc.settings')}</h2><p>{t('fc.help')}</p><div className="fc-actions"><Link className="s-btn s-btn--ghost" to="/extension">{t('fc.install')}</Link><Btn variant="ghost" disabled={loading || busy} onClick={refresh}>{t('fc.refresh')}</Btn></div></>}
        </CardBody></Card>
        {gagalT2 && <FlowGagalLogin kod={gagalT2} emel={auth.currentUser?.email} busy={busy} onRetry={() => void launch()} onClose={() => setGagalT2(null)} />}
    </main>;
}
